import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@edusense/shared-auth-core';
import { hasAdminAuthorizationHeader, refreshAdminAccessToken } from '../../auth/adminAccessToken';
import { ensureAdminRequestHeaders } from '../../auth/adminRequestHeaders';
import { buildRequestCacheKey, clearRequestCacheByPrefix, fetchWithTtlCache } from '../../lib/requestCache';
import {
  BUSINESS_METRICS_PATH,
  buildBusinessMetricsQuery,
  fetchBusinessMetrics,
  type BusinessGroupBy,
  type BusinessMetricsClient,
  type BusinessMetricsResponse,
} from '../../types/businessMetrics';
import {
  BUSINESS_METRICS_SESSION_EXPIRED_MESSAGE,
  describeBusinessMetricsError,
  responseStatus,
} from './businessMetricsErrors';

const BUSINESS_METRICS_CACHE_TTL_MS = 60_000;
const BUSINESS_METRICS_TIMEOUT_MS = 15_000;

const axiosClient: BusinessMetricsClient = {
  get: (url, config) => axios.get(url, { ...config, timeout: BUSINESS_METRICS_TIMEOUT_MS }),
};

export interface BusinessMetricsState {
  data: BusinessMetricsResponse | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useBusinessMetrics(
  applicationId: string,
  startDate: string,
  endDate: string,
  groupBy: BusinessGroupBy,
): BusinessMetricsState {
  const { token: accessToken } = useAuth();
  // 요청에 쓴 앱 id 로 묶어 둔다(서버가 돌려주는 applicationId 표기에 기대지 않는다).
  const [loaded, setLoaded] = useState<{ forApplicationId: string; response: BusinessMetricsResponse } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(async (): Promise<BusinessMetricsResponse> => {
    const query = buildBusinessMetricsQuery(applicationId, startDate, endDate, groupBy);
    const cacheKey = buildRequestCacheKey(BUSINESS_METRICS_PATH, { ...query });
    return fetchWithTtlCache(cacheKey, BUSINESS_METRICS_CACHE_TTL_MS, async () => {
      const headers = await ensureAdminRequestHeaders(accessToken);
      if (!hasAdminAuthorizationHeader(headers)) {
        throw new Error(BUSINESS_METRICS_SESSION_EXPIRED_MESSAGE);
      }
      try {
        return await fetchBusinessMetrics(query, headers, axiosClient);
      } catch (requestError) {
        if (responseStatus(requestError) !== 401) throw requestError;
        const refreshedToken = await refreshAdminAccessToken();
        if (!refreshedToken) throw requestError;
        return fetchBusinessMetrics(query, { ...headers, Authorization: `Bearer ${refreshedToken}` }, axiosClient);
      }
    });
  }, [accessToken, applicationId, endDate, groupBy, startDate]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    load()
      .then((response) => {
        if (!cancelled) setLoaded({ forApplicationId: applicationId, response });
      })
      .catch((loadError: unknown) => {
        if (cancelled) return;
        setLoaded(null);
        setError(describeBusinessMetricsError(loadError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [applicationId, load, reloadKey]);

  const reload = useCallback(() => {
    clearRequestCacheByPrefix(BUSINESS_METRICS_PATH);
    setReloadKey((key) => key + 1);
  }, []);

  // 앱을 바꾼 직후 이전 앱의 수치를 새 앱 이름 아래 보여 주지 않는다.
  const data = loaded?.forApplicationId === applicationId ? loaded.response : null;
  return { data, loading, error, reload };
}
